import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { setCrossAppCache } from '@/utils/cross-app-cache';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { actionCacheKey, actionKeys, parseAction, type Action } from './entity';

/**
 * Action read queries.
 *
 * Bulletproof-style: each read has an exported `getXQueryOptions` factory
 * that returns a plain `queryOptions(...)` config, plus a thin hook that
 * spreads it into `useSuspenseQuery` and accepts caller-side overrides
 * via `queryConfig`.
 *
 * Callers can:
 *   - Use the hook for the standard case: `useAction({ id })`
 *   - Override per call site: `useQuery({ ...getActionQueryOptions(...), staleTime: Infinity })`
 *   - Prefetch from a route loader: `qc.prefetchQuery(getActionQueryOptions(...))`
 *   - Compose with other queries: `useQueries({ queries: [opts1, opts2] })`
 */

const STALE_TIME = 60_000;

/** Long enough for Ember's composer route to run after Hydra's gate falls back to it. */
const ACTION_CACHE_TTL = 10_000;

/* ------------------------------------------------------------------------- */
/* getAction — by-id with sub-action sideload                                 */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getAction(workspaceId: number, actionId: string, qc: QueryClient): Promise<Action> {
  const data = await hydraUiApi.actions.get({
    params: {
      path: { environment_id: String(workspaceId), action_id: actionId },
    },
  });
  // Sub-actions (split-test variants) sideload via `_actions`.
  // Populate their detail caches so a follow-up `useAction(variantId)`
  // resolves from cache.
  for (const sub of data._actions ?? []) {
    qc.setQueryData(actionKeys.detail(workspaceId, sub.id), parseAction(sub));
  }
  if (data.action == null) {
    throw new Error('missing action in get action response');
  }
  // The raw payload, `_actions` included, not the parsed `Action`: Ember's
  // action adapter reads this entry on the composer fallback and hands it to
  // its serializer, which expects the API's shape. A later read overwrites it.
  setCrossAppCache({ key: actionCacheKey(workspaceId, actionId), value: data, ttl: ACTION_CACHE_TTL });
  return parseAction(data.action);
}

export const getActionQueryOptions = (workspaceId: number, actionId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: actionKeys.detail(workspaceId, actionId),
    queryFn: () => getAction(workspaceId, actionId, qc),
    staleTime: STALE_TIME,
  });

interface UseActionOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getActionQueryOptions>;
}

export const useAction = ({ id, queryConfig }: UseActionOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getActionQueryOptions(workspaceId, id, qc),
    ...queryConfig,
  });
};

interface UseActionOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled by-id hook (see `useCampaignOptional`).
 * For callers resolving an action outside a suspense boundary that may keep the
 * query dormant. `skipToken` (not `enabled: false`) so no cache slot is
 * registered while inactive; `retry: false` so a 404 surfaces immediately.
 *
 * @public
 */
export const useActionOptional = ({ workspaceId, id, enabled = true }: UseActionOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? actionKeys.detail(workspaceId, id) : ['action', 'inactive'],
    queryFn: active ? () => getAction(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};

// `useCampaignForAction` (the action → campaign FK follow) lives in
// `campaign/queries.ts` to avoid a circular import between the entity
// modules. The hook returns a `Campaign`, so co-locating it with the
// campaign reads is honest about where the data comes from.
